// Serial numbers in the app: reading units from the register and the register
// updates that go in the same commit as a receipt, sale, cancel or return.
import {
  normalizeSerial,
  serialId,
  warrantyUntil,
  type DocumentLineRow,
  type JsonValue,
  type SyncOp,
} from '@ims/core'
import type { LocalRepository } from '@ims/local'
import { serialOp, type RowContext, type SerialRow } from './rows.ts'

/** The serials stored on an invoice or credit note line. */
export function lineSerials(value: JsonValue | null | undefined): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []
}

/** Each serial's unit as stored now (null for serials the register doesn't know). */
export async function loadSerials(
  repo: Pick<LocalRepository, 'get'>,
  tenantId: string,
  productId: string,
  serials: readonly string[],
): Promise<Map<string, SerialRow | null>> {
  const out = new Map<string, SerialRow | null>()
  for (const s of serials) {
    const serial = normalizeSerial(s)
    const row = await repo.get('product_serials', serialId(tenantId, productId, serial))
    out.set(serial, row && !row.deleted_at ? row : null)
  }
  return out
}

/** Why a serial cannot be received (it is in stock already), or null. */
export function receiveProblem(serial: string, existing: SerialRow | null): string | null {
  return existing?.status === 'in_stock' ? `${serial} is already in stock` : null
}

/** Why a serial cannot be sold, or null. Unknown serials may be sold (not synced yet). */
export function saleProblem(serial: string, existing: SerialRow | null): string | null {
  if (existing?.status === 'sold') return `${serial} is already sold`
  if (existing?.status === 'written_off') return `${serial} was written off`
  return null
}

/** Marks each line's units sold to that line, with the warranty from the sale date. */
export function saleSerialOps(
  ctx: RowContext,
  lines: readonly Pick<DocumentLineRow, 'id' | 'product_id' | 'serials'>[],
  existing: ReadonlyMap<string, SerialRow | null>,
  invoiceDate: string,
  warrantyMonths: (productId: string) => number | null,
): SyncOp[] {
  const ops: SyncOp[] = []
  for (const line of lines) {
    if (!line.product_id) continue
    const months = warrantyMonths(line.product_id)
    for (const serial of lineSerials(line.serials)) {
      ops.push(
        serialOp(ctx, line.product_id, serial, existing.get(serial) ?? null, {
          status: 'sold',
          sold_line_id: line.id,
          warranty_until: months ? warrantyUntil(invoiceDate, months) : null,
        }),
      )
    }
  }
  return ops
}

/** Units of a cancelled invoice go back into stock (the sale stays on record). */
export function cancelSerialOps(ctx: RowContext, units: readonly SerialRow[]): SyncOp[] {
  return units
    .filter((u) => u.status === 'sold')
    .map((u) =>
      serialOp(ctx, u.product_id, u.serial, u, { status: 'in_stock', warranty_until: null }),
    )
}

/** Returned units: back in stock when restocked, otherwise written off. */
export function returnSerialOps(
  ctx: RowContext,
  lines: readonly Pick<DocumentLineRow, 'id' | 'product_id' | 'serials'>[],
  existing: ReadonlyMap<string, SerialRow | null>,
  restock: boolean,
): SyncOp[] {
  const ops: SyncOp[] = []
  for (const line of lines) {
    if (!line.product_id) continue
    for (const serial of lineSerials(line.serials)) {
      ops.push(
        serialOp(ctx, line.product_id, serial, existing.get(serial) ?? null, {
          status: restock ? 'in_stock' : 'written_off',
          returned_line_id: line.id,
          warranty_until: null,
        }),
      )
    }
  }
  return ops
}
